import styled from "@emotion/styled";
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from "react";
import { Field, useForm } from "react-final-form";
import { FieldArrayProps } from "react-final-form-arrays";
import { useSelector } from "react-redux";

import { Delete, Info } from "@livechat/design-system-icons";
import {
  Accordion,
  Button,
  DesignToken,
  Icon,
  SpacingToken,
  Text,
  TransitionDurationToken,
  TransitionTimingToken,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { AutomationType } from "routes/automations/constants";
import { OpenedAccordionIndex } from "routes/automations/workflows/interfaces";
import { selectAgentsAlphabetically } from "store/entities/agents";
import { selectCannedResponsesArray } from "store/entities/canned-responses";
import {
  IRuleActionMessage,
  IRuleActionSendFollowUp,
  IRuleActionSendRatingRequest,
} from "store/entities/rules/interfaces";
import { selectTagsAlphabetically } from "store/entities/tags";
import { selectTeamsAlphabetically } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { ChooseActionOrTriggerModal } from "../components/ChooseActionOrTriggerModal";
import { validateAction } from "../validators";

import { Action } from "./Action";
import { ActionAccordionLabel } from "./ActionAccordionLabel";
import { ActionMultilineLabel } from "./ActionMultilineLabel";
import { ActionFieldArrayElement } from "./interfaces";
import { ActionType } from "./types";
import { ACTION_EMPTY_STATE_TEXT, changeAction, defaultActions, isActionType } from "./utils";

const VALIDATION_ERROR: Record<AutomationType, string> = {
  [AutomationType.Rule]: "Make sure the condition is added correctly.",
  [AutomationType.Macro]: "Make sure the action is specified correctly.",
};

interface IProps {
  index: number;
  fields: FieldArrayProps<ActionFieldArrayElement, HTMLElement>;
  limitReached: boolean;
  isModalOpen: boolean;
  onModalClose: () => void;
  openedIndex: OpenedAccordionIndex;
  setOpenedIndex: Dispatch<SetStateAction<OpenedAccordionIndex>>;
  automationType: AutomationType;
}

export const ActionPicker: FC<IProps> = ({
  index,
  fields,
  limitReached,
  isModalOpen,
  onModalClose,
  openedIndex,
  setOpenedIndex,
  automationType,
}) => {
  const form = useForm();
  const isOpen = openedIndex ? openedIndex.type === "action" && openedIndex.index === index : false;

  const allTeams = useSelector(selectTeamsAlphabetically);
  const allAgents = useSelector(selectAgentsAlphabetically);
  const allCannedResponses = useSelector(selectCannedResponsesArray);
  const allTags = useSelector(selectTagsAlphabetically);
  const actionRef = useRef<HTMLDivElement>(null);
  const [shouldScroll, setShouldScroll] = useState(false);

  useEffect(() => {
    if (shouldScroll) {
      actionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      setShouldScroll(false);
    }
  }, [shouldScroll]);

  return (
    <>
      <Field<ActionFieldArrayElement> name={`actions.${index}`} key={index}>
        {({ input, meta }) => {
          const handleSelectAction = (type: ActionType): void => {
            // We want to reset the field state to avoid displaying the error message when the user selects the action
            form.resetFieldState(`actions.${index}`);
            // We want to blur to change touched property in previously opened accordion
            setOpenedIndex(prevIndex => {
              if (prevIndex?.type === "action") {
                form.blur(`actions.${prevIndex.index}`);
              }
              return { type: "action", index };
            });
            changeAction(input, defaultActions[type]);
            setShouldScroll(true);
            onModalClose();
          };

          const handleRemoveAction = (): void => {
            fields.remove(index);
            setOpenedIndex(null);
          };

          const handleModalClose = (): void => {
            fields.remove(index);
            onModalClose();
          };

          const handleAccordionOpen = (): void => {
            // We want to blur to change touched property in previously opened accordion
            setOpenedIndex(prevIndex => {
              if (prevIndex?.type === "action") {
                form.blur(`actions.${prevIndex.index}`);
              }
              return { type: "action", index };
            });
          };

          const handleAccordionClose = (): void => {
            // We want the touched state to be set to true when the user closes the accordion
            form.blur(`actions.${index}`);
            setOpenedIndex(null);
          };

          // We want to render the tile only if the action has specified type to avoid layout shift
          const shouldRenderTile = input.value?.type;

          // We want to display the empty state only if there is only one action and the tile is not rendered, to keep placeholder visible even when the user is adding a new action with modal
          const shouldDisplayPlaceholder = fields.length === 1 && !shouldRenderTile;

          // We want to open the modal only for tile without action set
          const shouldOpenModal = !shouldRenderTile && isModalOpen;

          // Touched is needed to display the error message when the user closes the accordion, dirty is needed to display the error message on the first render to emphasize the error if workflows / macros has invalid actions
          const displayError =
            (meta.touched || !meta.dirty) &&
            validateAction(input.value, allTeams, allAgents, allCannedResponses, allTags);

          const shouldDisplayMultilineLabel =
            isActionType<IRuleActionSendRatingRequest>(input, "sendRatingRequest") ||
            isActionType<IRuleActionSendFollowUp>(input, "sendFollowUp") ||
            isActionType<IRuleActionMessage>(input, "message");

          return (
            <>
              <AccordionWrapper ref={actionRef} visible={!!shouldRenderTile}>
                <Accordion
                  label={{
                    open: <Text bold>Edit action</Text>,
                    closed: <ActionAccordionLabel input={input} />,
                  }}
                  multilineElement={
                    shouldDisplayMultilineLabel ? (
                      <ActionMultilineLabel input={input} onAccordionOpen={handleAccordionOpen} />
                    ) : undefined
                  }
                  isOpen={isOpen}
                  onOpen={handleAccordionOpen}
                  onClose={handleAccordionClose}
                  kind={displayError ? "error" : "default"}
                  css={{ maxWidth: "650px" }}
                >
                  <Block marginLeft={withVar(SpacingToken.Spacing2)}>
                    <Action input={input} limitReached={limitReached} />
                  </Block>
                  {displayError && (
                    <Flex
                      alignItems="center"
                      gap={withVar(SpacingToken.Spacing1)}
                      marginLeft={withVar(SpacingToken.Spacing1)}
                      marginTop={withVar(SpacingToken.Spacing3)}
                    >
                      <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicNegative)} />
                      <Text size="sm" customColor={withVar(DesignToken.ContentBasicNegative)}>
                        {VALIDATION_ERROR[automationType]}
                      </Text>
                    </Flex>
                  )}
                </Accordion>
                <DeleteButton
                  className="delete-button"
                  size="compact"
                  kind="plain"
                  icon={<Icon source={Delete} />}
                  onClick={handleRemoveAction}
                  open={isOpen}
                />
              </AccordionWrapper>
              {shouldDisplayPlaceholder && <EmptyStateText size="sm">{ACTION_EMPTY_STATE_TEXT}</EmptyStateText>}

              <ChooseActionOrTriggerModal
                mode="add"
                type="action"
                onClose={handleModalClose}
                onSelect={handleSelectAction}
                isVisible={shouldOpenModal}
              />
            </>
          );
        }}
      </Field>
    </>
  );
};

const AccordionWrapper = styled.div<{ visible: boolean }>`
  display: ${({ visible }): string => (visible ? "flex" : "none")};
  padding-right: var(${SpacingToken.Spacing5});

  :hover .delete-button {
    opacity: 1;
    visibility: visible;
  }
`;

const EmptyStateText = styled(Text)`
  margin-bottom: var(${SpacingToken.Spacing3});
  color: var(${DesignToken.ContentBasicDisabled});
`;

const DeleteButton = styled(Button)<{ open: boolean }>`
  margin-top: var(${SpacingToken.Spacing4});
  margin-left: var(${SpacingToken.Spacing3});
  opacity: ${({ open }): number => (open ? 1 : 0)};
  visibility: ${({ open }): string => (open ? "visible" : "hidden")};
  transition:
    opacity var(${TransitionDurationToken.Fast2}) var(${TransitionTimingToken.EaseOut}),
    visibility var(${TransitionDurationToken.Fast2}) var(${TransitionTimingToken.EaseOut});
`;
